import type Flickity from "flickity";
import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { useLazyBackground } from "tsi-common-react/src/utils/lazyBackground";
import { t } from "ttag";

import type { GalleryCarouselBlock as GalleryCarouselBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Value = GalleryCarouselBlockValue;
type Column = NonNullable<Value["columns"]>[number];
type ColumnImage = NonNullable<Column["image"]>;

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/gallery-carousel-block.html`` (the tempurpedic-local
 * GalleryCarouselBlock).
 *
 * The DOM is reproduced exactly so the styling + carousel behavior match:
 *   - Flickity is initialized FROM WITHIN this component via the shared
 *     ``useFlickity`` hook (a client-only ``useEffect``), NOT from the global
 *     ``ui/gallery-carousel-block.ts`` -- because this island OWNS the
 *     ``.gallery-carousel-block__container`` DOM (``include_react`` /
 *     ``hydrateRoot`` replaces the SSR nodes the legacy global
 *     ``onReadyStateComplete`` initializer queried once, so that init ran
 *     against a stale/clobbered subtree and the slides shipped un-initialized
 *     -- stacked vertically -- under React). The hook is SSR-safe
 *     (dynamic-imports ``flickity`` only on the client; a
 *     top-level import would crash the SSR renderer) and tears down with
 *     ``safeDestroy``. Options match the legacy ``new Flickity(gallery, ...)``
 *     call verbatim (``watchCSS``, ``autoPlay: false``, ``imagesLoaded: true``,
 *     ``pageDots: false``, ``prevNextButtons: true``).
 *   - The ``onInit`` callback re-binds the legacy button-group behavior: a
 *     click delegate on ``.buttons`` calls ``flickity.select(index)`` and a
 *     ``select`` listener toggles ``is-selected`` / ``aria-current`` on the
 *     buttons -- imperative DOM toggling exactly as the legacy JS did (the
 *     buttons render with the first ``is-selected`` / ``aria-current="true"``
 *     as the static initial state). The per-slide
 *     ``.gallery-carousel-block__slide`` children and the ``.buttons > button``
 *     set (``aria-label="Slide N of M"``) are reproduced verbatim.
 *   - The lazy background (``lazy-background`` class + ``data-background-image``
 *     attribute) is self-managed by this component via the shared
 *     ``useLazyBackground`` hook (a per-element IntersectionObserver that also
 *     handles SSR/puppeteer eager-load), attached to the ``<section>`` -- so this
 *     component emits the same class + attribute (width-2000 rendition of the
 *     ``background_style.background_image``).
 *
 * The legacy ``{{ value.theme.theme }}`` class referenced a ``theme`` field that
 * does NOT exist on this block, so it always resolved to empty and is omitted.
 * ``header_content`` (``{{ value.header_content }}``) and each column's
 * ``content`` (``{{ column.content|richtext }}``) both serialize to the
 * ``<div class="rich-text">``-wrapped string, injected directly. Each column
 * image used ``{% wagtail_image column.image specs='width-600,height-400' %}``
 * (two renditions + srcset; eager ``<img>`` under SSR), reproduced here.
 */
const ColumnImageElem = (
    props: Immutable<{ image: ColumnImage }>,
): React.JSX.Element => {
    const image = props.image;
    const r = image.renditions;
    const srcSet = [r["width-600"], r["height-400"]]
        .map((rend) => (rend ? `${rend.url} ${rend.width}w` : ""))
        .filter(Boolean)
        .join(",");
    return (
        <img
            alt={image.title}
            src={r["width-600"]?.url ?? r["height-400"]?.url ?? image.url}
            srcSet={srcSet || undefined}
            loading="lazy"
            decoding="async"
        />
    );
};

export const GalleryCarouselBlock = (
    props: Immutable<{ value: GalleryCarouselBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const bgImage = v.background_style?.background_image;
    const bgImageUrl = bgImage
        ? (bgImage.renditions["width-2000"]?.url ?? bgImage.url)
        : null;
    const columns = v.columns ?? [];
    const totalSlides = columns.length;

    // Carousel init lives HERE (not in `client/src/ui/gallery-carousel-block.ts`):
    // this component owns the `.gallery-carousel-block__container` DOM, so it
    // owns its own Flickity lifecycle via the shared SSR-safe `useFlickity`
    // hook. Options mirror the legacy `new Flickity(gallery, {...})` call.
    // Defer the section's CSS background image until it scrolls into view (eager
    // under puppeteer/SSR) via the shared per-element hook.
    const sectionRef = React.useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const buttonsRef = React.useRef<HTMLDivElement>(null);
    const onInit = React.useCallback((flickity: Flickity) => {
        const buttonGroup = buttonsRef.current;
        if (!buttonGroup) {
            return;
        }
        // Legacy: a click delegate on `.buttons` -> `flickity.select(index)`.
        const onClick = (event: Event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const index = Array.prototype.indexOf.call(
                buttonGroup.children,
                target,
            );
            if (index >= 0) {
                flickity.select(index);
            }
        };
        // Legacy: on `select`, toggle `is-selected` / `aria-current` imperatively.
        const onSelect = (index: number) => {
            const selectedButton = buttonGroup.children[Number(index)];
            Array.from(buttonGroup.children).forEach((button) => {
                if (button !== selectedButton) {
                    button.classList.remove("is-selected");
                    button.setAttribute("aria-current", "false");
                } else {
                    button.classList.add("is-selected");
                    button.setAttribute("aria-current", "true");
                }
            });
        };
        buttonGroup.addEventListener("click", onClick);
        flickity.on("select", onSelect);
        return () => {
            buttonGroup.removeEventListener("click", onClick);
            flickity.off("select", onSelect);
        };
    }, []);
    useFlickity(containerRef, {
        options: {
            watchCSS: true,
            autoPlay: false,
            imagesLoaded: true,
            pageDots: false,
            prevNextButtons: true,
        },
        enabled: totalSlides > 1,
        deps: [totalSlides],
        onInit,
    });

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "gallery-carousel-block",
                v.background_style?.style ?? "",
                bgImageUrl ? "lazy-background" : "",
                getGlobalSpacingClasses(v.spacing),
            ])}
            data-background-image={bgImageUrl ?? undefined}
        >
            <div>
                <header
                    dangerouslySetInnerHTML={{
                        __html: v.header_content ?? "",
                    }}
                />
                <div
                    className="gallery-carousel-block__container"
                    ref={containerRef}
                >
                    {columns.map((column, i) => (
                        <div
                            className={concatClassNames([
                                "gallery-carousel-block__slide",
                                v.columns_background_style?.style ?? "",
                            ])}
                            key={i}
                        >
                            {column.image ? (
                                <ColumnImageElem image={column.image} />
                            ) : null}
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: column.content ?? "",
                                }}
                            />
                        </div>
                    ))}
                </div>

                <div className="buttons" ref={buttonsRef}>
                    {columns.map((_column, i) => {
                        const slideNumber = i + 1;
                        return (
                            <button
                                key={i}
                                className={i === 0 ? "is-selected" : undefined}
                                aria-current={i === 0 ? "true" : "false"}
                                aria-label={t`Slide ${slideNumber} of ${totalSlides}`}
                            />
                        );
                    })}
                </div>

                {v.page_link ? (
                    <a className="button" href={v.page_link.url}>
                        {t`Learn More`}
                    </a>
                ) : null}
            </div>
        </section>
    );
};
